import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHaptics } from '@/hooks/useHaptics';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { recheckLogtoRoles } from '@/lib/roleRevalidation';

/**
 * Full-screen lockout shown by <AdminGate> when the active build has
 * `VITE_ADMIN_GATE_ENABLED=true` and the authenticated QLASH user does not
 * carry `is_admin === true`. The CTA reuses `useLogtoAuth().signOut` so the
 * user can drop their session and re-authenticate with an admin account.
 *
 * Visual shape mirrors <Login /> for consistency — same dark background,
 * brand-coloured header.
 *
 * 🔴 **DUE azioni, e l'ordine e' cambiato il 2026-09-06.** Prima c'era solo «esci»: l'unico
 * modo di uscire da questa schermata dopo essersi visti assegnare un ruolo era **buttare via
 * la sessione** e rifare l'accesso. Ora la prima azione e' «Ricontrolla», che rilegge i ruoli
 * dal token corrente (stessa azione e stessa copy di `<RoleRequired />`): risolve il caso
 * comune — «me l'hanno appena dato» — senza costare niente.
 * ⚠️ «Esci» resta, perche' il caso «sono entrato con l'account sbagliato» esiste, ma NON e'
 * piu' il bottone piu' vistoso: l'azione distruttiva non deve essere la piu' facile da
 * premere quando una piu' economica risolve lo stesso problema.
 *
 * ⚠️ La copy e' RIUSATA da `roleGate.*`, non ricopiata: due frasi identiche in due chiavi
 * diverse divergono alla prima revisione, e a quel punto la stessa app direbbe due cose
 * diverse sulla stessa azione. Il COMPONENTE invece non si riusa — `<RoleRequired />` e' un
 * pannello dentro una schermata, questa e' una schermata intera con l'uscita.
 */
export function AccessDenied() {
  const { t } = useTranslation('general');
  const { signOut } = useLogtoAuth();
  const haptics = useHaptics();
  const [ricontrollato, setRicontrollato] = useState(false);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-bg p-6 text-text">
      <div className="flex flex-col items-center gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-brand">QLASH</h1>
        <h2 className="text-xl font-semibold text-fg">{t('accessDenied.title')}</h2>
        <p className="max-w-sm text-center text-sm text-fg-muted">
          {t('accessDenied.message')}
        </p>
      </div>

      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          data-tour="access-denied-recheck"
          onClick={() => {
            haptics.impact('light');
            setRicontrollato(true);
            recheckLogtoRoles();
          }}
          className="rounded-md bg-brand px-8 py-3 text-base font-medium text-brand-fg transition-opacity active:opacity-80"
        >
          {t('roleGate.recheck')}
        </button>
        {/* Il segno che il tap ha fatto qualcosa: se i ruoli fossero arrivati, questa
            schermata non sarebbe piu' qui. Senza, il bottone viene premuto dieci volte. */}
        {ricontrollato ? (
          <p className="max-w-sm text-center text-xs text-fg-muted">
            {t('roleGate.stillMissing')}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => signOut()}
          className="px-4 py-2 text-sm font-medium text-fg-muted underline transition-opacity active:opacity-80"
        >
          {t('accessDenied.signOut')}
        </button>
      </div>
    </div>
  );
}
